import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "flex flex-wrap items-center gap-2",
  "data-testid": "ds-attendance-kind-tabs"
}
const _hoisted_2 = /*#__PURE__*/_createElementVNode("span", { class: "text-primary30 text-sm font-bold whitespace-nowrap" }, "Categoria:", -1)
const _hoisted_3 = {
  class: "inline-flex rounded-full border border-neutral-400 p-0.5",
  role: "tablist",
  "aria-label": "Categoria do registro"
}
const _hoisted_4 = ["id", "data-testid", "aria-selected", "aria-controls", "tabindex", "onClick", "onKeydown"]
const _hoisted_5 = {
  key: 0,
  class: "text-xs font-bold"
}
const _hoisted_6 = /*#__PURE__*/_createElementVNode("span", { class: "text-xs text-neutral50" }, "A contagem respeita os demais filtros.", -1)

import { computed } from 'vue'
import { CONTAINER_KIND_LABELS, ContainerKind } from '@/constants/ds-attendance.constants'
import type { ContainerCountsByKind } from '@/Network/Services/DsAttendanceService'

/**
 * Contagem tolerante. O contrato final (B17 §1.1) é `{ total, folha, atividade, requests }`,
 * e é ele que o service tipa; aqui todos os campos seguem OPCIONAIS de propósito — um
 * backend que ainda não sirva algum deles deve deixar a aba sem número, nunca com número
 * errado. `requests` é da F3 e vem `0` na F2.
 */
export type KindTabCounts = Partial<ContainerCountsByKind>

interface KindTab {
  id: string
  label: string
  value: ContainerKind[] | null
  count: number | null
  active: boolean
  /** F3 — a aba recorta por `onlyRequests`, não por `kind`. */
  requests?: boolean
}


export default /*#__PURE__*/_defineComponent({
  __name: 'DsAttendanceKindTabs',
  props: {
    modelValue: {},
    counts: {},
    onlyRequests: { type: Boolean },
    panelId: {}
  },
  emits: ["update:modelValue", "update:onlyRequests"],
  setup(__props: any, { emit }) {

const props = __props;





/** Na aba "Solicitações" nenhuma das outras fica acesa — o recorte é exclusivo. */
const selected = computed<ContainerKind | null>(() =>
  props.onlyRequests ? null : (props.modelValue?.[0] ?? null)
)

const folhaCount = computed(() => props.counts?.folha ?? null)
const atividadeCount = computed(() => props.counts?.atividade ?? null)

/** `total` é o campo do contrato; sem ele, a soma das duas espécies. */
const allCount = computed(() => {
  const counts = props.counts
  if (!counts) return null
  if (typeof counts.total === 'number') return counts.total
  if (typeof counts.folha === 'number' && typeof counts.atividade === 'number') {
    return counts.folha + counts.atividade
  }
  return null
})

/**
 * A aba "Solicitações" NASCE oculta: na F2 o contrato (B10) fixa `requests = 0`, então
 * nenhuma checagem de versão é necessária — quando a F3 começar a devolver número, a aba
 * aparece sozinha.
 *
 * ...mas ela NÃO some enquanto for o recorte ativo (I1). O contador vive aqui e o recorte
 * vive no pai: preencher a última cobrança zerava `requests`, a aba desaparecia com
 * `onlyRequests` ainda ligado e o segmented control ficava sem nenhuma aba acesa — todas
 * com `tabindex="-1"`, fora do alcance do teclado, sobre uma lista recortada sem nada que
 * explicasse o recorte. Com o contador `0` visível o auditor vê onde está e sai por conta.
 */
const hasRequests = computed(() => (props.counts?.requests ?? 0) > 0)
const showRequestsTab = computed(() => hasRequests.value || props.onlyRequests === true)

const visibleTabs = computed<KindTab[]>(() => {
  const isRequestsTab = props.onlyRequests === true
  const tabs: KindTab[] = [
    {
      id: 'all',
      label: 'Todos',
      value: null,
      count: allCount.value,
      active: !isRequestsTab && selected.value === null
    },
    {
      id: 'folha',
      label: CONTAINER_KIND_LABELS[ContainerKind.FOLHA],
      value: [ContainerKind.FOLHA],
      count: folhaCount.value,
      active: selected.value === ContainerKind.FOLHA
    },
    {
      id: 'atividade',
      label: CONTAINER_KIND_LABELS[ContainerKind.ATIVIDADE],
      value: [ContainerKind.ATIVIDADE],
      count: atividadeCount.value,
      active: selected.value === ContainerKind.ATIVIDADE
    }
  ]

  // F3 — a aba só nasce quando a política começar a cobrar pernas faltantes. O recorte dela
  // é o atalho `onlyRequests` do backend (§3.1), NÃO o `kind`: por isso `value: null` (a aba
  // não manda `kind`) e a seleção viaja pelo `update:onlyRequests`.
  if (showRequestsTab.value) {
    tabs.push({
      id: 'requests',
      label: 'Solicitações',
      value: null,
      count: props.counts?.requests ?? null,
      active: isRequestsTab,
      requests: true
    })
  }

  return tabs
})

/**
 * Índice da aba tabulável. Sempre existe UMA: sem este fallback um estado em que nenhuma aba
 * está ativa deixaria o `tablist` inteiro com `tabindex="-1"` e inalcançável por teclado.
 */
const tabbableIndex = computed(() => {
  const index = visibleTabs.value.findIndex((tab) => tab.active)
  return index === -1 ? 0 : index
})

/**
 * Os dois recortes são mutuamente exclusivos, e os dois eventos saem SEMPRE juntos: trocar de
 * aba sem desligar o `onlyRequests` deixaria a fila mostrando solicitações sob o rótulo
 * "Folha", e o pai não tem como adivinhar que precisa zerar o outro lado.
 */
function select(value: ContainerKind[] | null, requests = false) {
  emit('update:onlyRequests', requests)
  emit('update:modelValue', requests ? null : value)
}

/**
 * Navegação de `tablist` (I17): num grupo de abas o Tab entra UMA vez e as setas trocam de
 * aba — por isso o `tabindex` roving acima. A ativação é automática (a seta já seleciona),
 * como no resto dos filtros desta fila, onde a escolha é imediata e reversível.
 *
 * `aria-controls` aponta para a listagem: a tela de Aprovações passou a dar um `id` estável
 * ao container da lista e a marcá-lo como `tabpanel`. Sem o `panelId` (uso fora daquela
 * tela) o atributo simplesmente não é emitido — apontar para um id inexistente é pior para
 * o leitor de tela do que não apontar.
 */
function onTabKeydown(event: KeyboardEvent, index: number) {
  const tabs = visibleTabs.value
  if (!tabs.length) return

  let nextIndex: number | null = null
  if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
    nextIndex = (index + 1) % tabs.length
  } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
    nextIndex = (index - 1 + tabs.length) % tabs.length
  } else if (event.key === 'Home') {
    nextIndex = 0
  } else if (event.key === 'End') {
    nextIndex = tabs.length - 1
  }
  if (nextIndex === null) return

  event.preventDefault()
  const target = event.currentTarget as HTMLElement | null
  const sibling = target?.parentElement?.children.item(nextIndex)
  if (sibling instanceof HTMLElement) sibling.focus()
  select(tabs[nextIndex].value, tabs[nextIndex].requests === true)
}

return (_ctx: any,_cache: any) => {
  return (_ctx.counts)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _hoisted_2,
        _createElementVNode("div", _hoisted_3, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleTabs.value, (tab, index) => {
            return (_openBlock(), _createElementBlock("button", {
              key: tab.id,
              type: "button",
              role: "tab",
              id: `ds-attendance-kind-tab-${tab.id}`,
              "data-testid": `ds-attendance-kind-tab-${tab.id}`,
              "aria-selected": tab.active,
              "aria-controls": _ctx.panelId || undefined,
              tabindex: index === tabbableIndex.value ? 0 : -1,
              class: _normalizeClass([
          'flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm whitespace-nowrap',
          tab.active
            ? 'is-active bg-primary30 text-white font-bold'
            : 'text-neutral-400 hover:bg-gray-100'
        ]),
              onClick: ($event: any) => (select(tab.value, tab.requests === true)),
              onKeydown: ($event: any) => (onTabKeydown($event, index))
            }, [
              _createTextVNode(_toDisplayString(tab.label) + " ", 1),
              (tab.count !== null)
                ? (_openBlock(), _createElementBlock("b", _hoisted_5, _toDisplayString(tab.count), 1))
                : _createCommentVNode("", true)
            ], 42, _hoisted_4))
          }), 128))
        ]),
        _hoisted_6
      ]))
    : _createCommentVNode("", true)
}
}

})